import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { accrualTypes, employees, plannedAccrualOrders } from '@/api/hr';
import { orgs } from '@/api/catalogs';
import PrintActions from '@/shared/components/PrintActions';
import { partyCodesText } from '@/shared/utils/partyCodes';

/**
 * Друкована форма наказа про зміну нарахувань (за патерном HireOrderPrintPage).
 *
 * Тіло: «З {дата} встановити нові розміри нарахувань наступним працівникам:» +
 * таблиця №/ПIБ/Таб.№/Вид/Спосіб/Значення. Route .../:id/print, window.print().
 */

const KIND_LABEL: Record<string, string> = {
  FIXED_SUM: 'Сума', PERCENT: '%', RATE: 'Тариф',
};

const fmtValue = (kind: string | null | undefined, value: string | null | undefined): string => {
  if (value == null) return '—';
  const n = Number(value);
  if (kind === 'PERCENT') return `${n}%`;
  if (kind === 'RATE') return `${n} (тариф)`;
  return `${n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} грн`;
};

const fmtDate = (v: string) => new Date(v).toLocaleDateString('uk-UA');

export default function PlannedAccrualOrderPrintPage() {
  const { id } = useParams();
  const docQ = useQuery({
    queryKey: ['planned-accrual-order', id ?? ''],
    queryFn: () => plannedAccrualOrders.get(id!),
    enabled: !!id,
  });
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const typesQ = useQuery({ queryKey: ['accrual-types'], queryFn: accrualTypes.list });
  // Таб.№ — з довідника співробітників юрособи наказа.
  const empQ = useQuery({
    queryKey: ['employees-for-pao-print', docQ.data?.organization_id ?? ''],
    queryFn: () => employees.list({ organization_id: docQ.data!.organization_id, limit: 1000 }),
    enabled: !!docQ.data,
  });

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  if (!docQ.data) return <div style={{ padding: '2rem' }}>Завантаження…</div>;
  const d = docQ.data;
  const org = orgsQ.data?.items.find((o) => o.id === d.organization_id);
  const typeName = new Map((typesQ.data ?? []).map((t) => [t.id, t.name]));
  const empCode = new Map((empQ.data?.items ?? []).map((e) => [e.id, e.code]));
  const empName = new Map((empQ.data?.items ?? []).map((e) => [e.id, e.full_name]));
  const items = [...d.items].sort((a, b) => (a.line_number ?? 0) - (b.line_number ?? 0));

  const cellTh: React.CSSProperties = {
    padding: '0.35rem 0.5rem', border: '1px solid #000', background: '#f0f0f0', textAlign: 'left',
  };
  const cell: React.CSSProperties = { padding: '0.35rem 0.5rem', border: '1px solid #000' };

  return (
    <div className="print-page" style={{
      padding: '15mm', fontFamily: 'Times, serif', fontSize: '11pt',
      maxWidth: '210mm', margin: '0 auto', background: 'var(--bg-panel)', lineHeight: 1.4,
    }}>
      <PrintActions docMeta={{
        organizationId: d.organization_id,
        documentType: 'doc.planned_accrual_orders',
        documentId: d.id,
        filename: `Наказ про зміну нарахувань ${d.document_number ?? ''}`.trim(),
        orgName: org?.name ?? null,
      }} />

      <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
        <div>{org?.full_name ?? org?.name ?? ''}</div>
        {partyCodesText(org) && <div style={{ fontSize: '9pt' }}>{partyCodesText(org)}</div>}
      </div>

      <h2 style={{ textAlign: 'center', fontSize: '14pt', margin: '1rem 0 0.3rem' }}>
        НАКАЗ № {d.document_number}
      </h2>
      <p style={{ textAlign: 'center', margin: 0 }}>від {fmtDate(d.document_date)}</p>
      <p style={{ textAlign: 'center', margin: '0.5rem 0', fontWeight: 600 }}>
        про зміну нарахувань
      </p>

      <p style={{ marginTop: '1.5rem' }}>
        З <strong>{fmtDate(d.effective_date)}</strong> встановити нові розміри нарахувань
        наступним працівникам:
      </p>

      <table style={{ width: '100%', marginTop: '1rem', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ ...cellTh, textAlign: 'center', width: '5%' }}>№</th>
            <th style={cellTh}>ПIБ</th>
            <th style={{ ...cellTh, textAlign: 'center', width: '15%' }}>Таб.№</th>
            <th style={cellTh}>Вид</th>
            <th style={cellTh}>Спосіб</th>
            <th style={{ ...cellTh, textAlign: 'right', width: '18%' }}>Значення</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={it.id ?? i}>
              <td style={{ ...cell, textAlign: 'center' }}>{i + 1}</td>
              <td style={cell}>{it.employee_name ?? empName.get(it.employee_id) ?? '—'}</td>
              <td style={{ ...cell, textAlign: 'center' }}>{empCode.get(it.employee_id) ?? '—'}</td>
              <td style={cell}>{typeName.get(it.accrual_type_id) ?? '—'}</td>
              <td style={cell}>{KIND_LABEL[it.value_kind ?? ''] ?? '—'}</td>
              <td style={{ ...cell, textAlign: 'right' }}>{fmtValue(it.value_kind, it.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <table style={{ width: '100%', marginTop: '3rem' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <div>Керівник:</div>
              <div style={{ marginTop: '2rem', borderTop: '1px solid #000', textAlign: 'center', width: '80%' }}>
                ___________________
              </div>
              <div style={{ fontSize: '8pt', textAlign: 'center', width: '80%' }}>(підпис, П.І.Б.)</div>
            </td>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <div>З наказом ознайомлені:</div>
              <div style={{ marginTop: '2rem', borderTop: '1px solid #000', textAlign: 'center', width: '80%' }}>
                ___________________
              </div>
              <div style={{ fontSize: '8pt', textAlign: 'center', width: '80%' }}>(підписи працівників)</div>
            </td>
          </tr>
        </tbody>
      </table>

      <style>{`
        @media print {
          .print-hide { display: none !important; }
        }
      `}</style>
    </div>
  );
}
